import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Download, 
  Calendar, 
  User, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Eye, 
  FileText,
  Pencil
} from 'lucide-react';
import { api } from '../../api/client';
import { getStatusBadge, formatUzPhone } from '../../utils/helpers';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import ConfirmationModal from '../../components/admin/ConfirmationModal';

export default function AdminAppointmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [appointments, setAppointments] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [lawyerFilter, setLawyerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Modals state
  const [selectedApt, setSelectedApt] = useState(null);
  const [modalType, setModalType] = useState('CONFIRM');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Edit Appointment Modal state (Admin feature: editing confirmed appointments)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  const [editFormData, setEditFormData] = useState({
    appointmentDate: '',
    startTime: '',
    status: 'CONFIRMED',
    lawyerId: '',
    officeId: '',
    adminNotes: '',
    clientName: '',
    clientPhone: '',
  });

  const handleOpenEdit = (apt) => {
    setEditingApt(apt);
    setEditFormData({
      appointmentDate: apt.appointmentDate || '',
      startTime: apt.startTime || '',
      status: apt.status || 'CONFIRMED',
      lawyerId: apt.lawyerId || apt.lawyer?.id || '',
      officeId: apt.officeId || apt.office?.id || '',
      adminNotes: apt.adminNote || apt.adminNotes || '',
      clientName: apt.clientName || '',
      clientPhone: apt.clientPhone || '',
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingApt) return;
    setActionLoading(true);
    try {
      const payload = {
        ...editFormData,
        lawyerId: editFormData.lawyerId ? Number(editFormData.lawyerId) : undefined,
        officeId: editFormData.officeId ? Number(editFormData.officeId) : undefined,
      };
      const res = await api.put(`/appointments/${editingApt.id}`, payload);
      if (res?.success) {
        showToast('Uchrashuv muvaffaqiyatli tahrirlandi!', 'success');
        setIsEditModalOpen(false);
        setEditingApt(null);
        loadAppointments(pagination.page);
      }
    } catch (err) {
      showToast(err.message || 'Tahrirlashda xatolik', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Load lawyers and offices for filter dropdowns
  useEffect(() => {
    Promise.all([api.get('/lawyers'), api.get('/offices')])
      .then(([lRes, oRes]) => {
        if (lRes?.success) setLawyers(lRes.lawyers || []);
        if (oRes?.success) setOffices(oRes.offices || []);
      })
      .catch(() => {});
  }, []);

  const loadAppointments = (page = 1) => {
    setLoading(true);
    let url = `/appointments?page=${page}&limit=20`;
    if (statusFilter && statusFilter !== 'ALL') url += `&status=${statusFilter}`;
    if (lawyerFilter) url += `&lawyerId=${lawyerFilter}`;
    if (searchQuery.trim()) url += `&search=${encodeURIComponent(searchQuery.trim())}`;
    if (dateFilter) url += `&date=${dateFilter}`;

    api.get(url)
      .then((res) => {
        if (res?.success) {
          setAppointments(res.appointments || []);
          if (res.pagination) setPagination(res.pagination);
        }
      })
      .catch((err) => {
        showToast(err.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadAppointments(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, lawyerFilter, dateFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadAppointments(1);
  };

  const handleOpenConfirm = (apt, type) => {
    setSelectedApt(apt);
    setModalType(type);
    setIsConfirmModalOpen(true);
  };

  const handleViewDetail = (apt) => {
    setSelectedApt(apt);
    setIsDetailModalOpen(true);
  };

  const handleModalAction = async (appointmentId, note) => {
    setActionLoading(true);
    try {
      let endpoint = `/appointments/${appointmentId}/confirm`;
      let payload = { adminNote: note };

      if (modalType === 'REJECT') {
        endpoint = `/appointments/${appointmentId}/reject`;
      } else if (modalType === 'CANCEL') {
        endpoint = `/appointments/${appointmentId}/cancel`;
        payload = { cancelReason: note, adminNote: note };
      }

      const res = await api.post(endpoint, payload);
      if (res?.success) {
        showToast(res.message, 'success');
        setIsConfirmModalOpen(false);
        setIsDetailModalOpen(false);
        loadAppointments(pagination.page);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    if (newStatus === 'CANCELLED') {
      const apt = appointments.find((a) => a.id === appointmentId);
      if (apt) {
        handleOpenConfirm(apt, 'CANCEL');
        return;
      }
    }
    try {
      const res = await api.post(`/appointments/${appointmentId}/status`, { status: newStatus });
      if (res?.success) {
        showToast(`Holat yangilandi: ${newStatus}`, 'success');
        loadAppointments(pagination.page);
        if (isDetailModalOpen) setIsDetailModalOpen(false);
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleExportCsv = async () => {
    try {
      showToast('CSV eksport tayyorlanmoqda...', 'info');
      const token = localStorage.getItem('advokat_token');
      const res = await fetch('/api/appointments/export', {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        throw new Error(`Eksport xatosi (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `uchrashuvlar_eksport_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('CSV fayl muvaffaqiyatli yuklab olindi!', 'success');
    } catch (err) {
      showToast(err.message || 'Eksportda xatolik yuz berdi', 'error');
    }
  };

  return (
    <div className="space-y-6 animate-admin-fade-in">
      {/* Filters Bar */}
      <div className="bg-white p-3.5 sm:p-4 rounded-lg border border-slate-200 shadow-2xs space-y-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-3">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Bron ID, ism, telefon, advokat yoki xizmat qidirish..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:border-[#c5a880]"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-slate-700 focus:outline-none w-full sm:w-auto"
            >
              <option value="ALL">Barcha holatlar</option>
              <option value="PENDING">Kutilmoqda (Pending)</option>
              <option value="CONFIRMED">Tasdiqlangan (Confirmed)</option>
              <option value="REJECTED">Rad etilgan (Rejected)</option>
              <option value="COMPLETED">Bajarilgan (Completed)</option>
              <option value="CANCELLED">Bekor qilingan (Cancelled)</option>
              <option value="NO_SHOW">Kelmadi (No-show)</option>
            </select>

            {/* Lawyer Filter */}
            <select
              value={lawyerFilter}
              onChange={(e) => setLawyerFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-slate-700 focus:outline-none w-full sm:w-auto"
            >
              <option value="">Barcha advokatlar</option>
              {lawyers.map((l) => (
                <option key={l.id} value={l.id}>{l.fullName}</option>
              ))}
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-slate-700 focus:outline-none w-full sm:w-auto"
            />

            <button
              type="submit"
              className="px-4 py-1.5 text-xs font-semibold bg-[#091322] text-[#c5a880] rounded hover:bg-[#11223b] transition-colors cursor-pointer w-full sm:w-auto text-center"
            >
              Qidirish
            </button>
          </div>

          {/* CSV Export Button */}
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-colors w-full sm:w-auto sm:ml-auto cursor-pointer"
          >
            <Download className="w-3.5 h-3.5 mr-1 text-slate-600" />
            CSV Eksport
          </button>
        </form>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto min-w-full">
          <table className="w-full text-left text-xs min-w-[960px]">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4 whitespace-nowrap">Bron ID</th>
                <th className="py-3 px-4 whitespace-nowrap">Mijoz</th>
                <th className="py-3 px-4 whitespace-nowrap">Advokat</th>
                <th className="py-3 px-4 whitespace-nowrap">Xizmat turi</th>
                <th className="py-3 px-4 whitespace-nowrap">Sana & Vaqt</th>
                <th className="py-3 px-4 whitespace-nowrap">Holat</th>
                <th className="py-3 px-4 text-right whitespace-nowrap min-w-[260px]">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    Yuklanmoqda...
                  </td>
                </tr>
              ) : appointments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">
                    Hech qanday uchrashuv arizalari topilmadi
                  </td>
                </tr>
              ) : (
                appointments.map((apt) => {
                  const badge = getStatusBadge(apt.status);
                  return (
                    <tr key={apt.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-slate-800 whitespace-nowrap tabular-nums">
                        {apt.bookingReference}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{apt.clientName}</div>
                        <div className="text-[11px] text-slate-500 font-mono whitespace-nowrap tabular-nums">{formatUzPhone(apt.clientPhone)}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-700 whitespace-nowrap">
                        {apt.lawyer?.fullName}
                      </td>
                      <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                        {apt.service?.titleUz}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-800 whitespace-nowrap tabular-nums">
                        {apt.appointmentDate} <span className="text-slate-400 font-sans">{apt.startTime}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.classes}`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${badge.dot}`} />
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center space-x-1.5">
                          <button
                            onClick={() => handleViewDetail(apt)}
                            className="p-1.5 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors cursor-pointer"
                            title="Batafsil ko‘rish"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Prominent Edit Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(apt)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-300 rounded shadow-2xs transition-colors cursor-pointer"
                            title="Uchrashuvni tahrirlash"
                          >
                            <Pencil className="w-3.5 h-3.5 text-amber-600" />
                            <span>Tahrirlash</span>
                          </button>

                          {apt.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => handleOpenConfirm(apt, 'CONFIRM')}
                                className="px-2.5 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors cursor-pointer"
                              >
                                Tasdiqlash
                              </button>
                              <button
                                onClick={() => handleOpenConfirm(apt, 'REJECT')}
                                className="px-2.5 py-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded transition-colors cursor-pointer"
                              >
                                Rad etish
                              </button>
                            </>
                          )}

                          {apt.status !== 'PENDING' && (
                            <select
                              value={apt.status}
                              onChange={(e) => handleStatusChange(apt.id, e.target.value)}
                              className="px-2 py-1 border border-slate-300 rounded text-[10px] bg-white text-slate-700 cursor-pointer font-medium hover:border-slate-400"
                              title="Holatni o‘zgartirish (Adashib bekor qilingan yoki tasdiqlangan bo‘lsa tuzatish)"
                            >
                              <option value="CONFIRMED">Tasdiqlangan</option>
                              <option value="PENDING">Kutilmoqda</option>
                              <option value="COMPLETED">Bajarildi</option>
                              <option value="NO_SHOW">Kelmadi</option>
                              <option value="CANCELLED">Bekor qilish</option>
                              <option value="REJECTED">Rad etish</option>
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
            <div>
              Jami: <strong>{pagination.total}</strong> ta ariza
            </div>
            <div className="flex space-x-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadAppointments(pagination.page - 1)}
                className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50"
              >
                Oldingi
              </button>
              <span className="px-3 py-1 font-semibold text-slate-800">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadAppointments(pagination.page + 1)}
                className="px-3 py-1 border border-slate-300 rounded disabled:opacity-50"
              >
                Keyingi
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Appointment Detail Modal */}
      {selectedApt && (
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title={`Uchrashuv Tafsilotlari (${selectedApt.bookingReference})`}
          maxWidth="max-w-2xl"
        >
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded border border-slate-200">
              <div>
                <span className="text-slate-500 block">Mijoz:</span>
                <span className="font-semibold text-slate-800 text-sm">{selectedApt.clientName}</span>
                <div className="text-slate-600 mt-1 font-mono">{formatUzPhone(selectedApt.clientPhone)}</div>
                <div className="text-slate-600">{selectedApt.clientEmail}</div>
              </div>

              <div>
                <span className="text-slate-500 block">Holat:</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border mt-1 ${getStatusBadge(selectedApt.status).classes}`}>
                  {getStatusBadge(selectedApt.status).label}
                </span>
                <div className="mt-2 text-slate-600">
                  Advokat: <strong>{selectedApt.lawyer?.fullName}</strong>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-slate-500 block">Qabul vaqti:</span>
                <span className="font-mono font-semibold text-slate-800 text-sm">
                  {selectedApt.appointmentDate} soat {selectedApt.startTime} - {selectedApt.endTime}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Ofis:</span>
                <span className="font-semibold text-slate-800">{selectedApt.office?.name}</span>
              </div>
            </div>

            <div>
              <span className="text-slate-500 block mb-1">Mijozning murojaat sababi:</span>
              <div className="p-3 rounded bg-slate-50 border border-slate-200 italic leading-relaxed text-slate-800">
                "{selectedApt.clientReason}"
              </div>
            </div>

            {selectedApt.adminNote && (
              <div>
                <span className="text-slate-500 block mb-1">Ma’muriyat eslatmasi (Admin Note):</span>
                <div className="p-3 rounded bg-amber-50 border border-amber-200 text-amber-900 leading-relaxed font-medium">
                  "{selectedApt.adminNote}"
                </div>
              </div>
            )}

            {selectedApt.cancelReason && (
              <div>
                <span className="text-slate-500 block mb-1">Bekor qilish sababi (Cancellation Reason):</span>
                <div className="p-3 rounded bg-rose-50 border border-rose-200 text-rose-900 leading-relaxed font-medium">
                  "{selectedApt.cancelReason}"
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  handleOpenEdit(selectedApt);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold rounded border border-slate-300 transition-colors cursor-pointer"
              >
                <Pencil className="w-3.5 h-3.5 text-amber-600" />
                Tahrirlash
              </button>

              <div className="flex items-center space-x-2">
                {selectedApt.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleOpenConfirm(selectedApt, 'CONFIRM');
                      }}
                      className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded cursor-pointer transition-colors"
                    >
                      Tasdiqlash
                    </button>
                    <button
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleOpenConfirm(selectedApt, 'REJECT');
                      }}
                      className="px-4 py-2 bg-rose-700 hover:bg-rose-800 text-white text-xs font-semibold rounded cursor-pointer transition-colors"
                    >
                      Rad etish
                    </button>
                  </>
                )}

                {selectedApt.status === 'CONFIRMED' && (
                  <button
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      handleOpenConfirm(selectedApt, 'CANCEL');
                    }}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded cursor-pointer transition-colors"
                  >
                    Bekor qilish (Izoh bilan)
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmation Note Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        appointment={selectedApt}
        type={modalType}
        onConfirm={handleModalAction}
        loading={actionLoading}
      />

      {/* Edit Appointment Modal (Allows editing confirmed and any appointments) */}
      {editingApt && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingApt(null);
          }}
          title={`Uchrashuvni Tahrirlash (${editingApt.bookingReference})`}
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Qabul Sanasi *</label>
                <input
                  type="date"
                  required
                  value={editFormData.appointmentDate}
                  onChange={(e) => setEditFormData({ ...editFormData, appointmentDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Boshlanish Vaqti *</label>
                <input
                  type="time"
                  required
                  value={editFormData.startTime}
                  onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Uchrashuv Holati *</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
                >
                  <option value="PENDING">Kutilmoqda (PENDING)</option>
                  <option value="CONFIRMED">Tasdiqlangan (CONFIRMED)</option>
                  <option value="COMPLETED">Yakunlandi (COMPLETED)</option>
                  <option value="NO_SHOW">Kelmadi (NO_SHOW)</option>
                  <option value="CANCELLED">Bekor qilingan (CANCELLED)</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Biriktirilgan Advokat</label>
                <select
                  value={editFormData.lawyerId}
                  onChange={(e) => setEditFormData({ ...editFormData, lawyerId: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
                >
                  <option value="">Advokatni tanlang...</option>
                  {lawyers.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Mijoz Ismi *</label>
                <input
                  type="text"
                  required
                  value={editFormData.clientName}
                  onChange={(e) => setEditFormData({ ...editFormData, clientName: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Mijoz Telefoni *</label>
                <input
                  type="text"
                  required
                  placeholder="+998 (97) 325-07-70"
                  value={editFormData.clientPhone || '+998 ()'}
                  onFocus={() => {
                    if (!editFormData.clientPhone) setEditFormData((p) => ({ ...p, clientPhone: '+998 ()' }));
                  }}
                  onChange={(e) => setEditFormData({ ...editFormData, clientPhone: formatUzPhone(e.target.value, editFormData.clientPhone) })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Filial / Ofis</label>
              <select
                value={editFormData.officeId}
                onChange={(e) => setEditFormData({ ...editFormData, officeId: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
              >
                <option value="">Ofisni tanlang...</option>
                {offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.city})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">
                Ma’muriyat Izohi / Eslatmasi (Admin Note)
              </label>
              <textarea
                rows={3}
                value={editFormData.adminNotes}
                onChange={(e) => setEditFormData({ ...editFormData, adminNotes: e.target.value })}
                placeholder="Uchrashuv haqida ichki eslatma yoki mijozga yuboriladigan xabar..."
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
              />
            </div>

            <div className="pt-3 border-t border-slate-200 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingApt(null);
                }}
                className="px-4 py-2 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-5 py-2 bg-[#091322] text-[#c5a880] hover:bg-[#12233b] font-semibold rounded cursor-pointer disabled:opacity-50"
              >
                {actionLoading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
